import { GroupPeopleIcon, HeadphoneIcon, MicIcon } from "@/assets/icons";
import { getAuthControllerGetCurrentUserQueryKey } from "@/lib/lutely-api/gen/auth/auth";
import type { UpdatePersonasDtoPersonasItem } from "@/lib/lutely-api/gen/dtos/updatePersonasDtoPersonasItem";
import { UpdatePersonasDtoPersonasItem as PersonaEnum } from "@/lib/lutely-api/gen/dtos/updatePersonasDtoPersonasItem";
import { useProfileControllerUpdatePersonas } from "@/lib/lutely-api/gen/profile/profile";
import * as Dialog from "@radix-ui/react-dialog";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { ComponentType, SVGProps } from "react";
import { useState } from "react";
import PersonaCard, { type PersonaAccent } from "./PersonaCard";

type PersonaCardConfig = {
  persona: UpdatePersonasDtoPersonasItem;
  accent: PersonaAccent;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
  destination: "/audiolab" | "/marketplace";
};

const CARDS: PersonaCardConfig[] = [
  {
    persona: PersonaEnum.listener,
    accent: "purple",
    icon: HeadphoneIcon,
    title: "Fan",
    description: "I listen to music",
    destination: "/marketplace",
  },
  {
    persona: PersonaEnum.artist,
    accent: "blue",
    icon: MicIcon,
    title: "Artist",
    description: "I make music",
    destination: "/audiolab",
  },
  {
    persona: PersonaEnum.label_rep,
    accent: "teal",
    icon: GroupPeopleIcon,
    title: "Label",
    description: "I work with multiple artists",
    destination: "/audiolab",
  },
];

export default function PersonaPicker() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activePersona, setActivePersona] =
    useState<UpdatePersonasDtoPersonasItem | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const updatePersonasMutation = useProfileControllerUpdatePersonas({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getAuthControllerGetCurrentUserQueryKey(),
        });
      },
    },
  });

  const persistPersonas = (personas: UpdatePersonasDtoPersonasItem[]) =>
    updatePersonasMutation.mutateAsync({ data: { personas } });

  const handleSelect = async (card: PersonaCardConfig) => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      await persistPersonas([card.persona]);
      navigate({ to: card.destination });
    } catch {
      setIsBusy(false);
    }
  };

  return (
    <Dialog.Root open>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          className="fixed inset-0 z-50 overflow-y-auto"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          {/* min-h-full wrapper centers the modal when it fits and lets the
              whole thing scroll from the top when it's taller than the viewport
              (mobile) — plain `items-center` on a scroll container clips the top. */}
          <div className="flex min-h-full items-center justify-center px-5 py-6">
            <div className="flex w-full max-w-[335px] flex-col items-center rounded-[20px] bg-white px-5 py-8 md:w-[960px] md:max-w-[90vw] md:px-14 md:pb-12 md:pt-14">
              <Dialog.Title className="text-center font-figtree text-[26px] font-extrabold leading-[1.2] tracking-[-0.4px] text-blue-900 md:text-[44px] md:tracking-[-0.8px]">
                Which best <br className="md:hidden" />
                describes you?
              </Dialog.Title>
              <Dialog.Description className="mt-3 text-center font-open-sans text-[14px] leading-[1.4] text-[#525966] md:mt-4 md:text-[18px]">
                This helps us personalize your experience.
              </Dialog.Description>

              <div
                className="mt-6 flex w-full flex-col gap-3 md:mt-12 md:flex-row md:items-stretch md:justify-center md:gap-6"
                onMouseLeave={() => setActivePersona(null)}
              >
                {CARDS.map((card) => (
                  <PersonaCard
                    key={card.persona}
                    accent={card.accent}
                    icon={card.icon}
                    title={card.title}
                    description={card.description}
                    active={activePersona === card.persona}
                    onActivate={() => setActivePersona(card.persona)}
                    onSelect={() => handleSelect(card)}
                    disabled={isBusy}
                    data-testid={`persona-card-${card.persona}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
